import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Plus, UserPlus, Upload, Download, Trash2, Edit, Search,
  FolderOpen, ChevronRight, Loader2, X, BookOpen, Shield, Route,
  ToggleLeft, ToggleRight, Eye, MoreVertical, BarChart3, KeyRound,
  Building2, CheckSquare, Sparkles, Phone, Briefcase, GraduationCap, Check
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const TeamPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [paths, setPaths] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showImportCsv, setShowImportCsv] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showGroupAssign, setShowGroupAssign] = useState(false);
  const [showM365Import, setShowM365Import] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDept, setFilterDept] = useState("all");

  const [memberForm, setMemberForm] = useState({ email: "", full_name: "", role: "learner", password: "Welcome123!", department: "", job_title: "", manager_id: "" });
  const [groupForm, setGroupForm] = useState({ name: "", description: "", member_ids: [] });
  const [csvText, setCsvText] = useState("");
  const [assignForm, setAssignForm] = useState({ type: "course", item_id: "" });
  const [showEditMember, setShowEditMember] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: "", role: "", department: "", job_title: "", phone: "", manager_id: "" });
  const [m365Step, setM365Step] = useState("choose");
  const [m365Provider, setM365Provider] = useState("");
  const [m365Users, setM365Users] = useState([]);
  const [m365Selected, setM365Selected] = useState([]);
  const [showBulkEnroll, setShowBulkEnroll] = useState(false);
  const [bulkEnrollCourses, setBulkEnrollCourses] = useState(new Set());
  const [bulkCourseSearch, setBulkCourseSearch] = useState("");
  const [bulkEnrolling, setBulkEnrolling] = useState(false);
  
  // Multi-select & Bulk actions
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkAction, setShowBulkAction] = useState(false);
  const [bulkAction, setBulkAction] = useState("");
  const [bulkDept, setBulkDept] = useState("");
  
  // Add Member Wizard
  const [wizardStep, setWizardStep] = useState(1);
  const [departments, setDepartments] = useState([]);

  useEffect(() => { fetchData(); fetchDepartments(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDepartments = async () => {
    try {
      const res = await api.get("/departments");
      setDepartments(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    try {
      const [usersRes, groupsRes, coursesRes, pathsRes, templatesRes] = await Promise.all([
        api.get("/users"), api.get("/groups"),
        api.get("/courses").catch(()=>({data:[]})),
        api.get("/learning-paths").catch(()=>({data:[]})),
        api.get("/compliance/templates").catch(()=>({data:[]}))
      ]);
      setMembers(usersRes.data || []);
      setGroups(groupsRes.data || []);
      setCourses(coursesRes.data || []);
      setPaths(pathsRes.data || []);
      setTemplates(templatesRes.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleAddMember = async () => {
    if (!memberForm.email || !memberForm.full_name) { toast.error("Email and name required"); return; }
    setSaving(true);
    try {
      await api.post("/teams/members", memberForm);
      toast.success("Member added");
      setShowAddMember(false);
      setMemberForm({ email: "", full_name: "", role: "learner", password: "Welcome123!" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleImportCsv = async () => {
    const lines = csvText.trim().split("\n").filter(l => l.trim());
    if (lines.length < 2) { toast.error("CSV must have header + data rows"); return; }
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    const rows = lines.slice(1).map(line => {
      const vals = line.split(",").map(v => v.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = vals[i] || ""; });
      return obj;
    });
    setSaving(true);
    try {
      const res = await api.post("/teams/import-csv", rows);
      toast.success(`Imported ${res.data.imported} members${res.data.errors.length ? `, ${res.data.errors.length} errors` : ""}`);
      setShowImportCsv(false); setCsvText(""); fetchData();
    } catch (e) { toast.error("Import failed"); }
    finally { setSaving(false); }
  };

  const downloadTemplate = () => {
    const csv = "email,full_name,role\njohn@company.com,John Smith,learner\njane@company.com,Jane Doe,learner";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "team_import_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCreateGroup = async () => {
    if (!groupForm.name) { toast.error("Group name required"); return; }
    setSaving(true);
    try {
      await api.post("/groups", groupForm);
      toast.success("Group created");
      setShowCreateGroup(false);
      setGroupForm({ name: "", description: "", member_ids: [] });
      fetchData();
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    try { await api.delete(`/groups/${id}`); toast.success("Deleted"); fetchData(); }
    catch (e) { toast.error("Failed"); }
  };

  const handleGroupAssign = async () => {
    if (!selectedGroup || !assignForm.item_id) return;
    setSaving(true);
    try {
      await api.post(`/groups/${selectedGroup.id}/assign`, assignForm);
      toast.success("Assigned to group members");
      setShowGroupAssign(false);
    } catch (e) { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const openEdit = (m) => {
    setEditUser(m);
    setEditForm({
      full_name: m.full_name || "", role: m.role || "learner",
      department: m.department || "", job_title: m.job_title || "",
      phone: m.phone || "", manager_id: m.manager_id || "",
    });
    setShowEditMember(true);
  };

  const handleEditUser = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await api.put(`/users/${editUser.id}`, editForm);
      toast.success("User updated");
      setShowEditMember(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleToggleStatus = async (m) => {
    const newStatus = (m.status || "active") === "active" ? "inactive" : "active";
    try {
      await api.put(`/users/${m.id}/status`, { status: newStatus });
      toast.success(`${m.full_name} ${newStatus === "active" ? "activated" : "deactivated"}`);
      fetchData();
    } catch (e) { toast.error("Failed"); }
  };

  const handleDeleteUser = async (m) => {
    if (!window.confirm(`Remove ${m.full_name} from your team? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${m.id}`);
      toast.success("User removed");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleBulkAction = async () => {
    if (selectedUsers.length === 0 || !bulkAction) return;
    setSaving(true);
    try {
      const payload = { user_ids: selectedUsers, action: bulkAction };
      if (bulkAction === "move_department") payload.department = bulkDept;
      await api.post("/users/bulk-action", payload);
      toast.success(`${bulkAction.replace("_", " ")} applied to ${selectedUsers.length} users`);
      setSelectedUsers([]);
      setShowBulkAction(false);
      setBulkAction("");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async (m) => {
    if (!window.confirm(`Send password reset email to ${m.email}?`)) return;
    try {
      await api.post(`/users/${m.id}/reset-password`);
      toast.success(`Reset email sent to ${m.email} (simulated)`);
    } catch (e) { toast.error("Failed to reset password"); }
  };

  const openBulkEnroll = () => {
    setBulkEnrollCourses(new Set());
    setBulkCourseSearch("");
    setShowBulkEnroll(true);
  };

  const toggleBulkCourse = (courseId) => {
    setBulkEnrollCourses(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      return next;
    });
  };

  const handleBulkEnroll = async () => {
    if (bulkEnrollCourses.size === 0 || selectedUsers.length === 0) return;
    setBulkEnrolling(true);
    let success = 0;
    let failed = 0;
    for (const userId of selectedUsers) {
      for (const courseId of bulkEnrollCourses) {
        try {
          await api.post("/enrollments", { user_id: userId, course_id: courseId });
          success++;
        } catch { failed++; }
      }
    }
    setBulkEnrolling(false);
    setShowBulkEnroll(false);
    const total = selectedUsers.length * bulkEnrollCourses.size;
    if (success > 0) toast.success(`${success} of ${total} enrollments created`);
    if (failed > 0) toast.error(`${failed} failed (may already be enrolled)`);
    setSelectedUsers([]);
  };

  const toggleSelectUser = (id) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredMembers.length) setSelectedUsers([]);
    else setSelectedUsers(filteredMembers.map(m => m.id));
  };

  const basePath = user?.role === "super_admin" ? "/admin" : "/tenant";

  const filteredMembers = members.filter(m => {
    const matchSearch = !searchQuery || m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || m.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchDept = filterDept === "all" || m.department === filterDept;
    return matchSearch && matchDept;
  });

  const learners = members.filter(m => m.role === "learner");
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  return (
    <AdminLayout title="Team" variant={variant}>
      <motion.div className="ent-page space-y-6" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="flex items-end justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="ent-icon-wrap" style={{ background: "rgba(59,130,246,0.08)" }}>
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" data-testid="team-title">Team Management</h2>
            </div>
            <p className="text-sm text-slate-500 ml-14">{members.length} members, {groups.length} groups, {departments.length} departments</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-xl border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300" onClick={() => navigate(`${basePath}/analytics`)} data-testid="team-analytics-btn">
              <BarChart3 className="w-4 h-4 mr-1.5" /> Analytics
            </Button>
            <Button variant="outline" className="rounded-xl border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300" onClick={() => setShowImportCsv(true)} data-testid="import-csv-btn"><Upload className="w-4 h-4 mr-2" /> CSV</Button>
            <Button className="ent-btn-primary" onClick={() => { setWizardStep(1); setMemberForm({ email: "", full_name: "", role: "learner", password: "", department: "", job_title: "", phone: "", manager_id: "" }); setShowAddMember(true); }} data-testid="add-member-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add Member
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="team-stats">
          {[
            { label: "Total Members", value: members.length, color: "#3b82f6", bg: "bg-blue-50", icon: Users },
            { label: "Enrolled in Courses", value: members.filter(m => m.enrolled_count > 0).length || learners.length, color: "#8b5cf6", bg: "bg-violet-50", icon: BookOpen },
            { label: "Enrollment Rate", value: `${learners.length > 0 ? Math.round(learners.filter(m => (m.enrolled_count || 0) > 0).length / learners.length * 100) : 0}%`, color: "#10b981", bg: "bg-emerald-50", icon: CheckSquare },
            { label: "Managers", value: members.filter(m => m.role === "manager" || m.role === "tenant_admin").length, color: "#f59e0b", bg: "bg-amber-50", icon: Briefcase },
          ].map((stat, i) => (
            <div key={i} className={`ent-card p-4 ${stat.bg}/50`} data-testid={`team-stat-${i}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-9 h-9 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className="w-4.5 h-4.5" style={{ color: stat.color }} />
                </div>
              </div>
              <p className="text-2xl font-extrabold" style={{ color: stat.color }}>{stat.value}</p>
              <p className="text-xs font-medium text-slate-500 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="members">
          <TabsList>
            <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
            <TabsTrigger value="groups">Groups ({groups.length})</TabsTrigger>
            <TabsTrigger value="departments">Departments ({departments.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="members" className="space-y-4 mt-4">
            <div className="flex gap-3 flex-wrap items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search members..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 rounded-xl border-slate-200 bg-white" />
              </div>
              <Select value={filterDept} onValueChange={setFilterDept}>
                <SelectTrigger className="w-[170px] rounded-xl border-slate-200" data-testid="dept-filter">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {[...new Set(members.map(m => m.department).filter(Boolean))].sort().map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-blue-50 text-blue-600">Admins: {members.filter(m=>m.role==="tenant_admin").length}</span>
                <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-violet-50 text-violet-600">Managers: {members.filter(m=>m.role==="manager").length}</span>
                <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600">Learners: {learners.length}</span>
              </div>
            </div>

            {/* Bulk Action Bar */}
            {selectedUsers.length > 0 && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20" data-testid="bulk-action-bar">
                <CheckSquare className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{selectedUsers.length} selected</span>
                <div className="flex-1" />
                <Select value={bulkDept} onValueChange={setBulkDept}>
                  <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue placeholder="Move to dept..." /></SelectTrigger>
                  <SelectContent>
                    {departments.map(d => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-8 text-xs text-primary" onClick={openBulkEnroll} data-testid="bulk-enroll">
                  <GraduationCap className="w-3 h-3 mr-1" /> Enroll
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!bulkDept} onClick={() => { setBulkAction("move_department"); handleBulkAction(); }} data-testid="bulk-move-dept">
                  <Building2 className="w-3 h-3 mr-1" /> Move
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs text-amber-600" onClick={() => { setBulkAction("deactivate"); handleBulkAction(); }} data-testid="bulk-deactivate">
                  <ToggleLeft className="w-3 h-3 mr-1" /> Deactivate
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs text-green-600" onClick={() => { setBulkAction("activate"); handleBulkAction(); }} data-testid="bulk-activate">
                  <ToggleRight className="w-3 h-3 mr-1" /> Activate
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-xs text-destructive" onClick={() => { if(window.confirm(`Delete ${selectedUsers.length} users?`)) { setBulkAction("delete"); handleBulkAction(); }}} data-testid="bulk-delete">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelectedUsers([])}><X className="w-3 h-3" /></Button>
              </motion.div>
            )}

            <div className="ent-card overflow-hidden">
              {/* Select all header */}
              <div className="flex items-center gap-3 p-4 border-b border-slate-100 bg-slate-50/80">
                <input type="checkbox" checked={selectedUsers.length === filteredMembers.length && filteredMembers.length > 0}
                  onChange={toggleSelectAll} className="rounded" data-testid="select-all-checkbox" />
                <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider flex-1">Name / Email</span>
                <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider w-28">Department</span>
                <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider w-24">Role</span>
                <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider w-40 text-right">Actions</span>
              </div>
              <div className="divide-y divide-slate-50">
                {filteredMembers.map(m => (
                  <div key={m.id} className={`flex items-center justify-between p-3.5 hover:bg-slate-50/70 group transition-colors relative ${selectedUsers.includes(m.id) ? "bg-blue-50/50" : ""}`} data-testid={`member-${m.id}`}>
                    {selectedUsers.includes(m.id) && <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-blue-500 rounded-r" />}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <input type="checkbox" checked={selectedUsers.includes(m.id)}
                        onChange={() => toggleSelectUser(m.id)} className="rounded shrink-0" />
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium shrink-0 cursor-pointer ${(m.status || "active") === "active" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}
                        onClick={() => navigate(`${basePath}/team/${m.id}`)}>
                        {(m.full_name || "?")[0].toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`${basePath}/team/${m.id}`)}>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm truncate">{m.full_name}</p>
                          {(m.status || "active") !== "active" && <Badge variant="outline" className="text-[10px] text-red-500 border-red-500/20">{m.status}</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                      </div>
                    </div>
                    <div className="w-28 shrink-0">
                      {m.department ? (
                        <Badge variant="outline" className="text-[10px]">{m.department}</Badge>
                      ) : (
                        <span className="text-[10px] text-muted-foreground/50">—</span>
                      )}
                    </div>
                    <div className="w-24 shrink-0">
                      <Badge variant="outline" className="text-[10px]">{m.role === "tenant_admin" ? "Business Admin" : m.role?.replace("_"," ")}</Badge>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 w-40 justify-end">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100" title="View" onClick={() => navigate(`${basePath}/team/${m.id}`)} data-testid={`view-${m.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100" title="Edit" onClick={() => openEdit(m)} data-testid={`edit-${m.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100" title="Reset Password" onClick={() => handleResetPassword(m)} data-testid={`reset-pwd-${m.id}`}>
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100" title="Toggle Status" onClick={() => handleToggleStatus(m)} data-testid={`toggle-${m.id}`}>
                        {(m.status || "active") === "active" ? <ToggleRight className="w-3.5 h-3.5 text-green-500" /> : <ToggleLeft className="w-3.5 h-3.5 text-red-500" />}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-destructive" title="Delete" onClick={() => handleDeleteUser(m)} data-testid={`delete-${m.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="groups" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button className="ent-btn-primary" onClick={() => setShowCreateGroup(true)} data-testid="create-group-btn"><Plus className="w-4 h-4 mr-2" /> New Group</Button>
            </div>

            {groups.length === 0 ? (
              <Card><CardContent className="py-12 text-center">
                <FolderOpen className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No groups yet</h3>
                <p className="text-muted-foreground mb-4">Create groups to bulk-assign courses and compliance</p>
                <Button onClick={() => setShowCreateGroup(true)}><Plus className="w-4 h-4 mr-2" /> Create Group</Button>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {groups.map(g => (
                  <Card key={g.id} className="card-hover" data-testid={`group-card-${g.id}`}>
                    <CardContent className="p-5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><FolderOpen className="w-5 h-5 text-primary" /></div>
                        <div>
                          <h3 className="font-semibold">{g.name}</h3>
                          <p className="text-sm text-muted-foreground">{g.member_count || 0} members{g.description ? ` · ${g.description}` : ""}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => { setSelectedGroup(g); setShowGroupAssign(true); }}><BookOpen className="w-4 h-4 mr-1" /> Assign</Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteGroup(g.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">{departments.length} departments in your organization</p>
            </div>
            {departments.length === 0 ? (
              <Card><CardContent className="text-center py-12">
                <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <h3 className="text-lg font-semibold text-slate-700 mb-1">No departments configured</h3>
                <p className="text-sm text-slate-400">Departments are created when members are assigned to them.</p>
              </CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {departments.map((dept, i) => {
                  const deptMembers = members.filter(m => m.department === dept.name);
                  const deptLearners = deptMembers.filter(m => m.role === "learner");
                  const enrolled = deptLearners.filter(m => (m.enrolled_count || 0) > 0).length;
                  const enrollRate = deptLearners.length > 0 ? Math.round(enrolled / deptLearners.length * 100) : 0;
                  return (
                    <div key={dept.id || i} className="ent-card p-5 hover:shadow-md transition-all cursor-pointer" data-testid={`dept-card-${i}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-bold text-slate-800 truncate">{dept.name}</h3>
                          <p className="text-xs text-slate-400">{deptMembers.length} members</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        <div className="p-2 rounded-lg bg-slate-50 text-center">
                          <p className="text-sm font-bold text-slate-700">{deptMembers.length}</p>
                          <p className="text-[9px] text-slate-400">Members</p>
                        </div>
                        <div className="p-2 rounded-lg bg-violet-50 text-center">
                          <p className="text-sm font-bold text-violet-600">{enrolled}</p>
                          <p className="text-[9px] text-slate-400">Enrolled</p>
                        </div>
                        <div className="p-2 rounded-lg bg-emerald-50 text-center">
                          <p className="text-sm font-bold text-emerald-600">{enrollRate}%</p>
                          <p className="text-[9px] text-slate-400">Enroll Rate</p>
                        </div>
                      </div>
                      {/* Department role breakdown */}
                      <div className="flex gap-2 mt-3 text-[10px]">
                        {deptMembers.filter(m => m.role === "manager").length > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">{deptMembers.filter(m => m.role === "manager").length} Managers</span>
                        )}
                        {deptLearners.length > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-semibold">{deptLearners.length} Learners</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* Add Member Wizard Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription className="sr-only">Add a new member to your team through a 3-step wizard</DialogDescription>
            <div className="flex items-center gap-2 mt-2">
              {[1, 2, 3].map(s => (
                <div key={s} className="flex items-center gap-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${wizardStep >= s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{s}</div>
                  <span className={`text-[10px] ${wizardStep >= s ? "text-foreground" : "text-muted-foreground"}`}>
                    {s === 1 ? "Personal" : s === 2 ? "Role & Dept" : "Review"}
                  </span>
                  {s < 3 && <ChevronRight className="w-3 h-3 text-muted-foreground" />}
                </div>
              ))}
            </div>
          </DialogHeader>
          {wizardStep === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              <div><Label>Full Name *</Label><Input value={memberForm.full_name} onChange={e=>setMemberForm({...memberForm,full_name:e.target.value})} placeholder="John Smith" data-testid="member-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={memberForm.email} onChange={e=>setMemberForm({...memberForm,email:e.target.value})} placeholder="john@company.com" data-testid="member-email-input" /></div>
              <div><Label>Phone</Label><Input value={memberForm.phone} onChange={e=>setMemberForm({...memberForm,phone:e.target.value})} placeholder="+1 555 0123" /></div>
              <p className="text-xs text-muted-foreground">A secure password will be auto-generated and sent via email.</p>
            </div>
          )}
          {wizardStep === 2 && (
            <div className="space-y-4" data-testid="wizard-step-2">
              <div><Label>Role *</Label>
                <Select value={memberForm.role} onValueChange={v=>setMemberForm({...memberForm,role:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="learner">Learner</SelectItem>
                    <SelectItem value="trainer">Trainer</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="tenant_admin">Business Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Department</Label>
                <Select value={memberForm.department || "none"} onValueChange={v=>setMemberForm({...memberForm,department:v==="none"?"":v})}>
                  <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Department</SelectItem>
                    {departments.map(d => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Job Title</Label><Input value={memberForm.job_title} onChange={e=>setMemberForm({...memberForm,job_title:e.target.value})} placeholder="Safety Officer" /></div>
              <div><Label>Manager</Label>
                <Select value={memberForm.manager_id || "none"} onValueChange={v=>setMemberForm({...memberForm,manager_id:v==="none"?"":v})}>
                  <SelectTrigger><SelectValue placeholder="No manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Manager</SelectItem>
                    {members.filter(m => m.role === "tenant_admin" || m.role === "manager").map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {wizardStep === 3 && (
            <div className="space-y-3" data-testid="wizard-step-3">
              <h4 className="font-medium text-sm">Review & Confirm</h4>
              <div className="rounded-lg border p-4 space-y-2 bg-muted/20">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Name</span><span className="font-medium">{memberForm.full_name || "—"}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Email</span><span className="font-medium">{memberForm.email || "—"}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Phone</span><span className="font-medium">{memberForm.phone || "—"}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Role</span><Badge variant="outline">{memberForm.role === "tenant_admin" ? "Business Admin" : memberForm.role}</Badge></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Department</span><span className="font-medium">{memberForm.department || "—"}</span></div>
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Job Title</span><span className="font-medium">{memberForm.job_title || "—"}</span></div>
              </div>
              <p className="text-xs text-muted-foreground bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
                A secure temporary password will be generated and sent to {memberForm.email || "the user's email"}. They will be prompted to change it on first login.
              </p>
            </div>
          )}
          <DialogFooter>
            {wizardStep > 1 && <Button variant="outline" onClick={() => setWizardStep(wizardStep - 1)}>Back</Button>}
            <div className="flex-1" />
            <Button variant="outline" onClick={()=>setShowAddMember(false)}>Cancel</Button>
            {wizardStep < 3 ? (
              <Button onClick={() => {
                if (wizardStep === 1 && (!memberForm.full_name || !memberForm.email)) { toast.error("Name and email are required"); return; }
                setWizardStep(wizardStep + 1);
              }} data-testid="wizard-next-btn">Next</Button>
            ) : (
              <Button onClick={handleAddMember} disabled={saving} data-testid="save-member-btn">
                {saving&&<Loader2 className="w-4 h-4 mr-2 animate-spin"/>}Add Member
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportCsv} onOpenChange={setShowImportCsv}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Team via CSV</DialogTitle>
            <DialogDescription>Upload a CSV file or paste CSV data with email, full_name, role columns</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
              <Download className="w-4 h-4 text-primary" />
              <span className="text-sm">Need a template?</span>
              <Button size="sm" variant="link" className="text-primary p-0 h-auto" onClick={downloadTemplate} data-testid="download-template-btn">Download CSV Template</Button>
            </div>
            <div>
              <Label>Paste CSV Data</Label>
              <Textarea rows={8} placeholder="email,full_name,role&#10;john@company.com,John Smith,learner&#10;jane@company.com,Jane Doe,learner" value={csvText} onChange={e=>setCsvText(e.target.value)} className="font-mono text-xs" data-testid="csv-input" />
            </div>
            <p className="text-xs text-muted-foreground">Or connect:
              <Button size="sm" variant="link" className="text-primary p-0 h-auto ml-1" onClick={() => { setShowImportCsv(false); setM365Provider("m365"); setM365Step("connecting"); setShowM365Import(true); }}>Microsoft 365</Button>
              <span className="mx-1">|</span>
              <Button size="sm" variant="link" className="text-primary p-0 h-auto" onClick={() => { setShowImportCsv(false); setM365Provider("google"); setM365Step("connecting"); setShowM365Import(true); }}>Google Workspace</Button>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowImportCsv(false)}>Cancel</Button>
            <Button onClick={handleImportCsv} disabled={saving||!csvText.trim()} data-testid="import-btn">{saving&&<Loader2 className="w-4 h-4 mr-2 animate-spin"/>}Import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create Group</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Group Name *</Label><Input value={groupForm.name} onChange={e=>setGroupForm({...groupForm,name:e.target.value})} data-testid="group-name-input" /></div>
            <div><Label>Description</Label><Textarea value={groupForm.description} onChange={e=>setGroupForm({...groupForm,description:e.target.value})} rows={2} /></div>
            <div>
              <Label>Members</Label>
              <div className="space-y-1 max-h-40 overflow-y-auto border rounded-lg p-2 mt-1">
                {learners.map(l=>(
                  <label key={l.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/30 cursor-pointer text-sm">
                    <input type="checkbox" checked={groupForm.member_ids.includes(l.id)} onChange={e=>{
                      if(e.target.checked) setGroupForm({...groupForm,member_ids:[...groupForm.member_ids,l.id]});
                      else setGroupForm({...groupForm,member_ids:groupForm.member_ids.filter(id=>id!==l.id)});
                    }} className="rounded" />
                    {l.full_name} <span className="text-muted-foreground text-xs">({l.email})</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowCreateGroup(false)}>Cancel</Button>
            <Button onClick={handleCreateGroup} disabled={saving} data-testid="save-group-btn">{saving&&<Loader2 className="w-4 h-4 mr-2 animate-spin"/>}Create Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Assign Dialog */}
      <Dialog open={showGroupAssign} onOpenChange={setShowGroupAssign}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign to Group: {selectedGroup?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Assignment Type</Label>
              <Select value={assignForm.type} onValueChange={v=>setAssignForm({...assignForm,type:v,item_id:""})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="course">Course</SelectItem>
                  <SelectItem value="learning_path">Learning Path</SelectItem>
                  <SelectItem value="compliance">Compliance Template</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Select Item</Label>
              <Select value={assignForm.item_id||"none"} onValueChange={v=>setAssignForm({...assignForm,item_id:v==="none"?"":v})}>
                <SelectTrigger><SelectValue placeholder="Choose..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select...</SelectItem>
                  {assignForm.type==="course" && courses.slice(0,30).map(c=><SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  {assignForm.type==="learning_path" && paths.map(p=><SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                  {assignForm.type==="compliance" && templates.map(t=><SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowGroupAssign(false)}>Cancel</Button>
            <Button onClick={handleGroupAssign} disabled={saving||!assignForm.item_id} data-testid="assign-group-btn">{saving&&<Loader2 className="w-4 h-4 mr-2 animate-spin"/>}Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEditMember} onOpenChange={setShowEditMember}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Full Name</Label><Input value={editForm.full_name} onChange={e => setEditForm({...editForm, full_name: e.target.value})} /></div>
            <div><Label>Role</Label>
              <Select value={editForm.role} onValueChange={v => setEditForm({...editForm, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="learner">Learner</SelectItem>
                  <SelectItem value="trainer">Trainer</SelectItem>
                  <SelectItem value="tenant_admin">Business Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Department</Label><Input value={editForm.department} onChange={e => setEditForm({...editForm, department: e.target.value})} placeholder="Engineering" /></div>
              <div><Label>Job Title</Label><Input value={editForm.job_title} onChange={e => setEditForm({...editForm, job_title: e.target.value})} placeholder="Safety Officer" /></div>
            </div>
            <div><Label>Phone</Label><Input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} placeholder="+1 555 0123" /></div>
            <div><Label>Manager</Label>
              <Select value={editForm.manager_id || "none"} onValueChange={v => setEditForm({...editForm, manager_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="No manager" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {members.filter(m => m.role === "tenant_admin" || m.role === "trainer").map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.full_name} ({m.role === "tenant_admin" ? "Business Admin" : "Trainer"})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMember(false)}>Cancel</Button>
            <Button onClick={handleEditUser} disabled={saving} data-testid="save-edit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* M365/Google Workspace Import Dialog */}
      <Dialog open={showM365Import} onOpenChange={(v) => { if (!v) { setShowM365Import(false); setM365Step("choose"); setM365Users([]); setM365Selected([]); } else setShowM365Import(v); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {m365Provider === "m365" ? "Microsoft 365" : "Google Workspace"} Import
            </DialogTitle>
          </DialogHeader>

          {m365Step === "connecting" && (
            <div className="py-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center bg-primary/10">
                {m365Provider === "m365" ? (
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none"><rect x="1" y="1" width="10" height="10" rx="1" fill="#F25022"/><rect x="13" y="1" width="10" height="10" rx="1" fill="#7FBA00"/><rect x="1" y="13" width="10" height="10" rx="1" fill="#00A4EF"/><rect x="13" y="13" width="10" height="10" rx="1" fill="#FFB900"/></svg>
                ) : (
                  <svg className="w-8 h-8" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                )}
              </div>
              <div>
                <p className="font-medium">Connecting to {m365Provider === "m365" ? "Microsoft 365" : "Google Workspace"}...</p>
                <p className="text-sm text-muted-foreground mt-1">Authenticating with your organization</p>
              </div>
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
              {/* Simulate connection after 2 seconds */}
              {(() => {
                setTimeout(() => {
                  if (m365Step === "connecting") {
                    const fakeUsers = [
                      { id: "m1", name: "Alex Johnson", email: "alex.j@company.com", department: "Engineering", selected: false },
                      { id: "m2", name: "Maya Patel", email: "maya.p@company.com", department: "Operations", selected: false },
                      { id: "m3", name: "Carlos Rivera", email: "carlos.r@company.com", department: "Safety", selected: false },
                      { id: "m4", name: "Diana Chen", email: "diana.c@company.com", department: "Maintenance", selected: false },
                      { id: "m5", name: "Robert Kim", email: "robert.k@company.com", department: "Quality", selected: false },
                      { id: "m6", name: "Sarah Thompson", email: "sarah.t@company.com", department: "Engineering", selected: false },
                      { id: "m7", name: "David Brown", email: "david.b@company.com", department: "Operations", selected: false },
                    ];
                    setM365Users(fakeUsers);
                    setM365Step("users");
                  }
                }, 2000);
                return null;
              })()}
            </div>
          )}

          {m365Step === "users" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{m365Users.length} users found in your organization</p>
                <Button size="sm" variant="outline" onClick={() => setM365Selected(m365Selected.length === m365Users.length ? [] : m365Users.map(u => u.id))}>
                  {m365Selected.length === m365Users.length ? "Deselect All" : "Select All"}
                </Button>
              </div>
              <div className="space-y-1 max-h-[300px] overflow-y-auto border rounded-lg p-2">
                {m365Users.map(u => (
                  <label key={u.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/30 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={m365Selected.includes(u.id)}
                      onChange={e => {
                        if (e.target.checked) setM365Selected([...m365Selected, u.id]);
                        else setM365Selected(m365Selected.filter(id => id !== u.id));
                      }}
                      className="rounded"
                    />
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary shrink-0">
                      {u.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{u.name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">{u.department}</Badge>
                  </label>
                ))}
              </div>
            </div>
          )}

          {m365Step === "importing" && (
            <div className="py-8 text-center space-y-4">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="font-medium">Importing {m365Selected.length} users...</p>
              <p className="text-sm text-muted-foreground">Adding users to your team</p>
              {(() => {
                setTimeout(() => {
                  if (m365Step === "importing") {
                    setM365Step("done");
                  }
                }, 2500);
                return null;
              })()}
            </div>
          )}

          {m365Step === "done" && (
            <div className="py-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
                <Shield className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <p className="font-medium text-lg">Import Complete!</p>
                <p className="text-sm text-muted-foreground mt-1">{m365Selected.length} users have been added to your team</p>
              </div>
              <p className="text-xs text-muted-foreground bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                Note: This is a simulated import. In production, this would sync with your {m365Provider === "m365" ? "Microsoft 365" : "Google Workspace"} directory via OAuth.
              </p>
            </div>
          )}

          <DialogFooter>
            {m365Step === "users" && (
              <>
                <Button variant="outline" onClick={() => { setShowM365Import(false); setM365Step("choose"); }}>Cancel</Button>
                <Button
                  onClick={() => setM365Step("importing")}
                  disabled={m365Selected.length === 0}
                  data-testid="m365-import-btn"
                >
                  Import {m365Selected.length} Users
                </Button>
              </>
            )}
            {m365Step === "done" && (
              <Button onClick={() => { setShowM365Import(false); setM365Step("choose"); setM365Users([]); setM365Selected([]); fetchData(); }}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Enroll Modal */}
      <Dialog open={showBulkEnroll} onOpenChange={setShowBulkEnroll}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" /> Bulk Enroll — {selectedUsers.length} User{selectedUsers.length !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>Select courses to enroll the selected team members in.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-1.5 mb-1">
            {selectedUsers.slice(0, 5).map(uid => {
              const m = members.find(x => x.id === uid);
              return m ? (
                <Badge key={uid} variant="outline" className="text-[10px]">{m.full_name?.split(" ")[0]}</Badge>
              ) : null;
            })}
            {selectedUsers.length > 5 && <Badge variant="outline" className="text-[10px]">+{selectedUsers.length - 5} more</Badge>}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search courses..."
              value={bulkCourseSearch}
              onChange={e => setBulkCourseSearch(e.target.value)}
              className="pl-9"
              data-testid="bulk-course-search"
            />
          </div>

          {bulkEnrollCourses.size > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <Badge className="bg-primary/10 text-primary">{bulkEnrollCourses.size} course{bulkEnrollCourses.size !== 1 ? "s" : ""} selected</Badge>
              <button className="text-xs text-muted-foreground hover:text-primary underline" onClick={() => setBulkEnrollCourses(new Set())}>Clear all</button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto min-h-0 space-y-1.5 -mx-1 px-1" style={{ maxHeight: "340px" }}>
            {courses.filter(c =>
              c.title?.toLowerCase().includes(bulkCourseSearch.toLowerCase()) ||
              c.category?.toLowerCase().includes(bulkCourseSearch.toLowerCase())
            ).map(c => {
              const isSelected = bulkEnrollCourses.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggleBulkCourse(c.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                    isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "hover:bg-muted/50 cursor-pointer"
                  }`}
                  data-testid={`bulk-course-${c.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? "bg-primary" : "bg-muted"}`}>
                    {isSelected ? <Check className="w-4 h-4 text-white" /> : <BookOpen className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.title}</p>
                    <p className="text-xs text-muted-foreground">{c.category || "General"} · {c.course_type?.includes("scorm") ? "eLearning" : c.course_type || "Course"}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <DialogFooter className="border-t pt-3">
            <div className="flex-1 text-xs text-muted-foreground">
              {bulkEnrollCourses.size > 0 && selectedUsers.length > 0 && (
                <span>{selectedUsers.length} users × {bulkEnrollCourses.size} courses = <strong>{selectedUsers.length * bulkEnrollCourses.size}</strong> enrollments</span>
              )}
            </div>
            <Button variant="outline" onClick={() => setShowBulkEnroll(false)}>Cancel</Button>
            <Button
              onClick={handleBulkEnroll}
              disabled={bulkEnrollCourses.size === 0 || bulkEnrolling}
              data-testid="confirm-bulk-enroll"
            >
              {bulkEnrolling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <GraduationCap className="w-4 h-4 mr-1" />}
              Enroll All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default TeamPage;
